import React, { useEffect } from "react";
import LightThemePage from "../../components/layout/LightThemePage";
import { Box, CircularProgress, CssBaseline, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import useHubspotTracking from "../../hooks/useHubspotTracking";
import { useAuth } from "../../hooks/useAuth";
import { useQuery } from "react-query";
import { StableData } from "../../api/StableData";
import { IStableDataDto, Pages } from "../../shared/types/Stabledata.type";
import { t } from "i18next";
import { get, isEmpty, isNull } from "lodash";
import TimeToPage from "./TimeToPage";
import FitnessPage from "./FitnessPage";
import StrideFrequencyPage from "./StrideFrequencyPage";
import SpeedAvgBest from "./SpeedAvgBest";
import EffortZoneTrainingTypePage from "./EffortZoneTrainingTypePage";
import EffortZoneTrack from "./EffortZoneTrack";
import GeneralUsePage from "./GeneralUsePage";
import IntroPage from "./IntroPage";
import StableAvgPage from "./StableAvgPage";
import CountryAvgPage from "./CountryAvgPage";
import { PageWrapper } from "./BgWrapper";
import FitnessBubbleD3 from "./FitnessBubbleD3";

function StableReview() {
  let [searchParams] = useSearchParams();
  //expected data from query params
  // token:user jwt
  //user:user id as string
  //start_date:date
  //end_date:date
  //Pages:string with comma for what pages to show on the pdf
  const user = searchParams.get("user");
  const start_date = searchParams.get("start_date");
  const end_date = searchParams.get("end_date");
  const pages = searchParams.get("pages");
  const TOKEN = searchParams.get("token") || localStorage.getItem("token");

  const ages = searchParams.get("horse_ages");
  const trackNames = searchParams.get("track_names");
  const trainingTypes = searchParams.get("training_types");
  const userHorses = searchParams.get("horses");
  const configuration_id = searchParams.get("configuration_id");
  const maxBest600 = searchParams.get("max_best_600");

  //States
  const { setToken, getUser, isUserFetching } = useAuth();

  //To hide chat banner in this page
  useHubspotTracking();

  //Effect

  useEffect(() => {
    const prevColor = document.body.style.backgroundColor;
    document.body.style.backgroundColor = "#f0f0f0"; // your desired color

    return () => {
      document.body.style.backgroundColor = prevColor; // restore when leaving the page
    };
  }, []);

  useEffect(() => {
    //if token defined set
    if (TOKEN) {
      setToken(TOKEN);
      getUser();
    }
  }, [TOKEN]);

  const preparePayload = (): null | IStableDataDto => {
    if (!pages || !user) return null;

    const splitedPages = pages.split(",");

    const formattedPages = preparePagesForPayload(splitedPages);

    const horse_ages =
      ages && typeof ages === "string"
        ? ages.split(",").map((age) => parseInt(age))
        : [];
    const track_names =
      trackNames && typeof trackNames === "string"
        ? trackNames.split(",").map((id) => parseInt(id))
        : [];
    const training_types =
      trainingTypes && typeof trainingTypes === "string"
        ? trainingTypes.split(",").map((id) => parseInt(id))
        : [];
    const horses =
      userHorses && typeof userHorses === "string"
        ? userHorses.split(",").map((id) => parseInt(id))
        : [];

    return {
      user,
      start_date,
      end_date,
      horse_ages,
      track_names,
      training_types,
      horses,
      ...(configuration_id
        ? { configuration_id: parseInt(configuration_id) }
        : {}),
      ...(maxBest600 ? { max_best_600: parseInt(maxBest600) } : {}),
      pages: formattedPages,
    };
  };

  const preparePagesForPayload = (pages: string[]) => {
    return pages.map((page, index) => ({
      type: page,
      order_index: ++index,
    }));
  };

  const { data, isLoading, isError } = useQuery(
    ["stable_review"],
    () => StableData.getStableData(preparePayload()),
    { enabled: !isUserFetching && !isNull(preparePayload()) },
  );

  const data_pages = data?.data?.pages || [];
  const data_insight_params = data?.data?.insight_parameters || [];
  const username = data?.data?.user?.full_name || "";

  const renderPages = (page: Pages) => {
    switch (page.type) {
      case "fitness_index":
        return (
          <FitnessBubbleD3 data={page} insightParams={data_insight_params} />
        );
      case "time_to":
        return <TimeToPage data={page} insightParams={data_insight_params} />;
      case "stride_frequencies":
        return (
          <StrideFrequencyPage
            data={page}
            insightParams={data_insight_params}
          />
        );
      case "speed_averages_best":
        return <SpeedAvgBest data={page} insightParams={data_insight_params} />;
      case "effort_zones_training_types":
        return (
          <EffortZoneTrainingTypePage
            data={page}
            insightParams={data_insight_params}
          />
        );
      case "effort_zones_tracks":
        return (
          <EffortZoneTrack data={page} insightParams={data_insight_params} />
        );
      case "general_use":
        return (
          <GeneralUsePage data={page} insightParams={data_insight_params} />
        );
      case "stable_averages":
        return (
          <StableAvgPage data={page} insightParams={data_insight_params} />
        );
      case "country_averages":
        return (
          <CountryAvgPage
            username={username}
            data={page}
            insightParams={data_insight_params}
          />
        );
      default:
        return null;
    }
  };

  const isInvalidParams = isNull(preparePayload());
  const isLoadingState = isUserFetching || isLoading;

  if (isLoadingState || isInvalidParams || isError) {
    return (
      <LightThemePage>
        <CssBaseline />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          minHeight="100vh" // Ensures vertical centering on screen
        >
          {isLoadingState && <CircularProgress />}

          {!isLoadingState && isInvalidParams && (
            <Typography variant="subtitle2" color="text.secondary">
              Please verify the query parameters then retry
            </Typography>
          )}

          {!isLoadingState && !isInvalidParams && isError && (
            <Typography variant="subtitle2" color="error">
              {t("error:generic")}
            </Typography>
          )}    
        </Box>
      </LightThemePage>
    );
  }

  return (
    <LightThemePage>
      <CssBaseline />
      <div data-testid="stable-review-content" data-loaded={!isLoadingState}>
        <PageWrapper backgroundImage="/bg-stable.jpg">
          <IntroPage
            stableName={data?.data?.user?.stable_name}
            startDate={start_date}
            endDate={end_date}
            repportName={get(
              data?.data,
              "send_stable_review_configuration.name",
            )}
          />
        </PageWrapper>
        {!isEmpty(data_pages) && (
          <div className="report_wrapper">
            {data_pages.map((page, index) => {
              return (
                <PageWrapper backgroundImage="/bg-stable.jpg">
                  {renderPages(page)}
                  <Box
                    sx={{
                      pageBreakAfter:
                        index !== data_pages.length - 1 ? "always" : "avoid",
                    }}
                  ></Box>
                </PageWrapper>
              );
            })}
          </div>
        )}
      </div>
    </LightThemePage>
  );
}

export default StableReview;
